import { Button, Flex, Grid, Icon } from "@chakra-ui/react";
import { useState } from "react";

import { useSlide } from "../public/slide";
import {
  OptionPropsCommon,
  SlidePropsMulti,
  SlidePropsSingle,
  SelectorValue,
} from "../public/types";
import { OptionSimple, OptionWithPicture } from "./option";
import { FaAngleDown } from "react-icons/fa6";

export type OptionRendererProps = {
  handleOptionClick: (value: SelectorValue) => void;
  isOptionSelected: (value: SelectorValue) => boolean;
  maxItems?: number;
};

function getOptionValue(option: OptionPropsCommon) {
  return option.value ?? option.text;
}

function Wrapper({
  direction,
  children,
}: React.PropsWithChildren<{ direction: "row" | "column" | "grid" }>) {
  if (direction === "grid") {
    return (
      <Grid width={"full"} gap={3} gridTemplateColumns={"1fr 1fr"}>
        {children}
      </Grid>
    );
  }

  return (
    <Flex
      width={"full"}
      flexDir={direction}
      flexWrap={direction === "row" ? "wrap" : undefined}
      gap={3}
      justifyContent={direction === "row" ? "center" : undefined}
    >
      {children}
    </Flex>
  );
}

export function CommonSelect({
  handleOptionClick,
  isOptionSelected,
  maxItems,
}: OptionRendererProps) {
  const slideCtx = useSlide() as SlidePropsMulti | SlidePropsSingle;
  const [isExpanded, setIsExpanded] = useState(false);

  if (slideCtx.variant === "list") {
    const dir = slideCtx.direction ?? "column";
    const options = slideCtx.options ?? [];
    const hasHidden = !!maxItems && !isExpanded && options.length > maxItems;
    const visibleOptions = hasHidden ? options.slice(0, maxItems) : options;

    return (
      <Flex width="full" flexDir="column" gap={3}>
        <Wrapper direction={dir}>
          {visibleOptions.map((option, idx) => {
            const value = getOptionValue(option);
            const optionValue: SelectorValue = { value, idx };
            return (
              <OptionSimple
                key={value}
                isSelected={isOptionSelected(optionValue)}
                onClick={() => handleOptionClick(optionValue)}
                text={option.text}
                icon={option.icon}
                selectorIconType={slideCtx.type === "single" ? "radio" : "checkbox"}
                size={slideCtx.size}
                {...option.styleProps}
              />
            );
          })}
        </Wrapper>
        {hasHidden && (
          <Button
            variant="solid"
            color="white"
            bg="whiteAlpha.300"
            px={8}
            py={5}
            mx="auto"
            mb={4}
            size={"sm"}
            borderRadius={"xl"}
            onClick={() => setIsExpanded(true)}
          >
            Show more
            <Icon ml={2} as={FaAngleDown} />
          </Button>
        )}
      </Flex>
    );
  }

  const imgHeight = slideCtx.size === "small" ? 100 : 180;

  return (
    <Flex gap={4} flexWrap={"wrap"} justifyContent={"center"}>
      {slideCtx.options?.map((option, idx) => {
        const value = getOptionValue(option);
        const optionValue: SelectorValue = { value: value, idx };
        return (
          <OptionWithPicture
            key={value}
            isSelected={isOptionSelected(optionValue)}
            onClick={() => handleOptionClick(optionValue)}
            text={option.text}
            imgHeight={imgHeight}
            imgComponent={option.imgComponent}
            hideText={slideCtx.hideText}
            width={"45%"}
            {...option.styleProps}
          />
        );
      })}
    </Flex>
  );
}
